import React, { useEffect, useMemo, useState, useCallback } from "react";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { createEmptyBlockItem, getColorHex } from "./blockTypeConstants";
import {
  addDealSection,
  addDealSectionMeta,
  deleteDealSectionMeta,
  getDealSectionMeta,
  getDealSectionOptions,
  updateDealSection,
} from "../../../redux/slices/DealApprovalSlice";
import TextForm from "./FormComponents/TextForm";
import BulletListForm from "./FormComponents/BulletListForm";
import CardGridForm from "./FormComponents/CardGridForm";
import ProgressBarForm from "./FormComponents/ProgressBarForm";
import MetricsForm from "./FormComponents/MetricsForm";
import StatusForm from "./FormComponents/StatusForm";
import UploadDocumentsForm from "./FormComponents/UploadDocumentsForm";
import "../DealApproval.scss";
import CustomButton from "../../../components/CustomButton/Button";
import CancelButton from "../../../components/CustomButton/CancelButton";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import { isValidMeaningfulData } from "./helpers";

const AddSectionModal = ({
  open,
  onClose,
  onCreate,
  initialData = null,
  dealId,
  currentSections = [],
}) => {
  const dispatch = useDispatch();
  const {
    sections: sectionMeta,
    blockTypes,
    options,
    dealSectionOptions,
    loading,
    addDealSectionMetaLoading,
    deleteDealSectionMetaLoading,
    addDealSectionLoading,
    updateDealSectionLoading,
  } = useSelector((state) => state.dealApproval);
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedTitle, setSelectedTitle] = useState("");
  const [selectedBlockType, setSelectedBlockType] = useState("");
  const [blockItems, setBlockItems] = useState([]);
  const [selectedBulletColor, setSelectedBulletColor] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [fieldTouched, setFieldTouched] = useState({
    selectedSection: false,
    selectedTitle: false,
    selectedBlockType: false,
  });
  const [showInlineSectionAdd, setShowInlineSectionAdd] = useState(false);
  const [inlineSectionNames, setInlineSectionNames] = useState([]);
  const isEditMode = Boolean(initialData);
  const allSections = useMemo(() => {
    const merged = [...(Array.isArray(sectionMeta) ? sectionMeta : [])];
    const seen = new Set();

    return merged.filter((section) => {
      const name = String(section?.sectionName || "")
        .trim()
        .toLowerCase();
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });
  }, [sectionMeta]);
  const selectedSectionMeta = allSections.find(
    (section) => section.sectionName === selectedSection,
  );
  const sortedSections = [...allSections].sort(
    (firstSection, secondSection) =>
      (firstSection?.displayOrder || 0) - (secondSection?.displayOrder || 0),
  );
  const nonDeletableSectionNames = useMemo(
    () =>
      new Set(["overview", "business model", "financials", "karncy analyses", "why to invest"]),
    [],
  );
  const dropdownSections = useMemo(
    () => [
      ...sortedSections.filter((section) =>
        nonDeletableSectionNames.has(
          String(section?.sectionName || "")
            .trim()
            .toLowerCase(),
        ),
      ),
      ...sortedSections
        .filter(
          (section) =>
            !nonDeletableSectionNames.has(
              String(section?.sectionName || "")
                .trim()
                .toLowerCase(),
            ),
        )
        .sort(
          (firstSection, secondSection) =>
            (secondSection?.displayOrder || 0) -
            (firstSection?.displayOrder || 0),
        ),
    ],
    [sortedSections, nonDeletableSectionNames],
  );

  const dropdownSectionMap = useMemo(
    () =>
      new Map(
        dropdownSections.map((section) => [
          String(section?.sectionName || "")
            .trim()
            .toLowerCase(),
          section,
        ]),
      ),
    [dropdownSections],
  );
  const canSubmitInlineSections =
    inlineSectionNames.length > 0 &&
    inlineSectionNames.every((name) => {
      const trimmedName = String(name || "").trim();
      return (
        trimmedName.length >= 4 &&
        trimmedName.length <= 150 &&
        /[A-Za-z]/.test(trimmedName)
      );
    });

  const renderSectionDropdownOption = (option) => {
    const sectionName = String(
      option?.sectionName || option?.label || option?.value || "",
    ).trim();
    const section =
      dropdownSectionMap.get(sectionName.toLowerCase()) ||
      option?.data ||
      option;
    const isDeletable =
      Boolean(sectionName) &&
      !nonDeletableSectionNames.has(sectionName.toLowerCase());

    return (
      <div className="section-option-row">
        <span className="section-option-name">{sectionName}</span>
        {isDeletable && (
          <button
            type="button"
            className="section-option-delete"
            disabled={loading || deleteDealSectionMetaLoading}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleDeleteSectionFromDropdown(section);
            }}
            aria-label={`Delete section ${sectionName}`}
          >
            <DeleteOutlined />
          </button>
        )}
      </div>
    );
  };
  const blockTypeOptions = [...blockTypes]
    .sort((firstBlockType, secondBlockType) => {
      const firstOrder =
        Number(String(firstBlockType?.blockTypeId || "").replace(/\D/g, "")) ||
        0;
      const secondOrder =
        Number(String(secondBlockType?.blockTypeId || "").replace(/\D/g, "")) ||
        0;
      return firstOrder - secondOrder;
    })
    .map((blockType) => blockType.blockTypeName);

  const allOptions = dealSectionOptions?.allOptions?.length
    ? dealSectionOptions.allOptions
    : options;
  const sortedByDisplayOrder = (items = []) =>
    [...items].sort((a, b) => (a?.displayOrder || 0) - (b?.displayOrder || 0));
  const normalizedType = (type) => String(type || "").toUpperCase();

  const bulletColorOptions = sortedByDisplayOrder(
    dealSectionOptions?.bulletColors?.length
      ? dealSectionOptions.bulletColors
      : allOptions.filter(
          (o) => normalizedType(o.optionType) === "BULLET_COLOR",
        ),
  );
  const prefixTypeOptions = sortedByDisplayOrder(
    dealSectionOptions?.bulletPrefixes?.length
      ? dealSectionOptions.bulletPrefixes
      : allOptions.filter(
          (o) => normalizedType(o.optionType) === "BULLET_PREFIX",
        ),
  );
  const metricPrefixOptions = sortedByDisplayOrder(
    dealSectionOptions?.metricPrefixes?.length
      ? dealSectionOptions.metricPrefixes
      : allOptions.filter(
          (o) => normalizedType(o.optionType) === "METRIC_PREFIX",
        ),
  );
  const metricSuffixOptions = sortedByDisplayOrder(
    dealSectionOptions?.metricSuffixes?.length
      ? dealSectionOptions.metricSuffixes
      : allOptions.filter(
          (o) => normalizedType(o.optionType) === "METRIC_SUFFIX",
        ),
  );
  const statusOptions = sortedByDisplayOrder(
    dealSectionOptions?.statusOptions?.length
      ? dealSectionOptions.statusOptions
      : allOptions.filter((o) => normalizedType(o.optionType) === "STATUS"),
  );

  // Current options API does not expose separate icon/bar families, so these reuse existing sets.
  const iconTypeOptions = prefixTypeOptions;
  const iconColorOptions = bulletColorOptions;
  const barColorOptions = bulletColorOptions;
  const defaultBulletColor = bulletColorOptions[0]?.optionValue || "";
  const defaultPrefixType = prefixTypeOptions[0]?.optionValue || "";
  const defaultMetricPrefix = metricPrefixOptions[0]?.optionValue || "";
  const defaultMetricSuffix = metricSuffixOptions[0]?.optionValue || "";
  const defaultStatus = statusOptions[0]?.optionValue || "";

  useEffect(() => {
    if (!open || !dealId) return;
    dispatch(getDealSectionMeta(dealId));
  }, [open, dealId, dispatch]);

  const resetForm = useCallback(() => {
    setSelectedSection("");
    setSelectedTitle("");
    setSelectedBlockType("");
    setBlockItems([]);
    setSelectedBulletColor(defaultBulletColor);
    setShowInlineSectionAdd(false);
    setInlineSectionNames([]);
    setSubmitAttempted(false);
    setFieldTouched({
      selectedSection: false,
      selectedTitle: false,
      selectedBlockType: false,
    });
  }, [defaultBulletColor]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    if (initialData) {
      setSelectedSection(initialData.sectionName || "");
      setSelectedTitle(initialData.title || "");
      setSelectedBlockType(initialData.blockType || "");
      setBlockItems(
        Array.isArray(initialData.blocks) ? initialData.blocks : [],
      );
      setSelectedBulletColor(initialData.bulletColor || defaultBulletColor);
      setSubmitAttempted(false);
      setFieldTouched({
        selectedSection: false,
        selectedTitle: false,
        selectedBlockType: false,
      });
      return;
    }

    resetForm();
  }, [open, initialData, defaultBulletColor, resetForm]);

  useEffect(() => {
    if (!selectedBulletColor && defaultBulletColor) {
      setSelectedBulletColor(defaultBulletColor);
    }
  }, [selectedBulletColor, defaultBulletColor]);

  const getBlockDefaults = () => ({
    prefixType: defaultPrefixType,
    iconType: defaultPrefixType,
    iconColor: defaultBulletColor,
    barColor: defaultBulletColor,
    metricPrefix: defaultMetricPrefix,
    metricSuffix: defaultMetricSuffix,
    status: defaultStatus,
  });

  const handleBlockTypeChange = (blockType) => {
    setSelectedBlockType(blockType);
    setFieldTouched((prev) => ({ ...prev, selectedBlockType: true }));
    setBlockItems(
      blockType ? [createEmptyBlockItem(blockType, getBlockDefaults())] : [],
    );
    if (blockType) {
      // Fetch all option groups to keep cross-dependent dropdowns populated.
      dispatch(getDealSectionOptions());
    }
    if (blockType !== "Bullet list") {
      setSelectedBulletColor(defaultBulletColor);
    }
  };

  const handleAddBlockItem = () => {
    if (!selectedBlockType) return;
    setBlockItems((prev) => {
      const nextItem = createEmptyBlockItem(
        selectedBlockType,
        getBlockDefaults(),
      );

      if (selectedBlockType === "Bullet list") {
        const sharedPrefixType = prev[0]?.prefixType || defaultPrefixType;
        return [...prev, { ...nextItem, prefixType: sharedPrefixType }];
      }

      return [...prev, nextItem];
    });
  };

  const handleDeleteBlockItem = (itemIndex) => {
    setBlockItems((prev) => prev.filter((_, idx) => idx !== itemIndex));
  };

  const updateBlockItem = (itemIndex, key, value) => {
    setBlockItems((prev) => {
      if (selectedBlockType === "Bullet list" && key === "prefixType") {
        return prev.map((item) => ({ ...item, prefixType: value }));
      }

      return prev.map((item, idx) =>
        idx === itemIndex ? { ...item, [key]: value } : item,
      );
    });
  };

  const getDuplicateTitleContentError = () => {
    if (isEditMode) return "";
    if (
      ![
        "Text",
        "Bullet list",
        "Status",
        "Metrics",
        "Card grid",
        "Progress bar",
        "Upload documents",
      ].includes(selectedBlockType)
    )
      return "";

    if (selectedBlockType === "Bullet list") {
      const incomingBulletTexts = Array.isArray(blockItems)
        ? blockItems
            .map((item) =>
              String(item?.item || "")
                .trim()
                .toLowerCase(),
            )
            .filter(Boolean)
        : [];
      const hasDuplicateWithinIncoming =
        new Set(incomingBulletTexts).size !== incomingBulletTexts.length;
      if (hasDuplicateWithinIncoming) return "Bullet list point already added.";
    }

    const trimmedTitle = (selectedTitle || "").trim();
    if (!trimmedTitle || !selectedSection) return "";

    const matchingSection = currentSections.find(
      (section) => section.sectionName === selectedSection,
    );
    const existingContentWithTitle =
      Array.isArray(matchingSection?.contents) &&
      matchingSection.contents.find(
        (content) =>
          String(content?.title || "")
            .trim()
            .toLowerCase() === trimmedTitle.toLowerCase(),
      );

    if (!existingContentWithTitle) return "";
    if (
      String(existingContentWithTitle?.blockType || "") !==
      String(selectedBlockType)
    )
      return "";

    if (
      ["Metrics", "Card grid", "Progress bar", "Status", "Upload documents"].includes(
        selectedBlockType,
      )
    ) {
      const keyByBlockType = {
        Metrics: "metricTitle",
        "Card grid": "blockTitle",
        "Progress bar": "label",
        Status: "statusTitle",
        "Upload documents": "fileName",
      };
      const compareKey = keyByBlockType[selectedBlockType];
      const existingTitles = Array.isArray(existingContentWithTitle?.blocks)
        ? existingContentWithTitle.blocks
            .map((block) =>
              String(block?.[compareKey] || "")
                .trim()
                .toLowerCase(),
            )
            .filter(Boolean)
        : [];
      const incomingTitles = Array.isArray(blockItems)
        ? blockItems
            .map((item) =>
              String(item?.[compareKey] || "")
                .trim()
                .toLowerCase(),
            )
            .filter(Boolean)
        : [];

      const isSameSection =
        incomingTitles.length > 0 &&
        incomingTitles.every((title) => existingTitles.includes(title));
      if (isSameSection) return "This section already exists.";
      return "";
    }

    const contentKeyByBlockType = {
      Text: "text",
      "Bullet list": "item",
      Status: "description",
    };
    const contentKey = contentKeyByBlockType[selectedBlockType];
    if (!contentKey) return "";

    const existingTexts = Array.isArray(existingContentWithTitle?.blocks)
      ? existingContentWithTitle.blocks
          .map((block) =>
            String(block?.[contentKey] || "")
              .trim()
              .toLowerCase(),
          )
          .filter(Boolean)
      : [];
    const incomingTexts = Array.isArray(blockItems)
      ? blockItems
          .map((item) =>
            String(item?.[contentKey] || "")
              .trim()
              .toLowerCase(),
          )
          .filter(Boolean)
      : [];
    const hasExactDuplicateText = incomingTexts.some((text) =>
      existingTexts.includes(text),
    );

    if (!hasExactDuplicateText) return "";
    if (selectedBlockType === "Bullet list")
      return "Same Bullet list points should not exists";
    if (selectedBlockType === "Card grid")
      return "Same card block title should not exists";
    return "Title and textarea description already exists";
  };

  const handleAddInlineSectionRow = () => {
    setShowInlineSectionAdd(true);
    setInlineSectionNames((prev) => [...prev, ""]);
  };

  const handleInlineSectionNameChange = (index, value) => {
    const nextValue = String(value || "");
    if (nextValue.length > 150) return;

    // First non-space character must be a letter.
    if (/^\s*[^A-Za-z]/.test(nextValue)) return;

    setInlineSectionNames((prev) =>
      prev.map((sectionName, idx) => (idx === index ? nextValue : sectionName)),
    );
  };

  const handleDeleteInlineSectionRow = (index) => {
    setInlineSectionNames((prev) => {
      const updatedRows = prev.filter((_, idx) => idx !== index);

      if (!updatedRows.length) {
        setShowInlineSectionAdd(false);
        return [];
      }

      return updatedRows;
    });
  };

  const handleSectionDropdownOpenChange = (isOpen) => {
    if (!isOpen) {
      setShowInlineSectionAdd(false);
      setInlineSectionNames([]);
    }
  };

  const handleAddSectionInsideDropdown = async () => {
    const trimmedSectionNames = inlineSectionNames
      .map((name) => String(name || "").trim())
      .filter(Boolean);

    if (!trimmedSectionNames.length) {
      return;
    }

    const tooShortSectionName = trimmedSectionNames.find(
      (sectionName) => sectionName.length < 4,
    );
    if (tooShortSectionName) {
      return;
    }

    const tooLongSectionName = trimmedSectionNames.find(
      (sectionName) => sectionName.length > 150,
    );
    if (tooLongSectionName) {
      return;
    }

    const invalidSectionName = trimmedSectionNames.find(
      (sectionName) => !/[A-Za-z]/.test(sectionName),
    );
    if (invalidSectionName) {
      return;
    }

    const normalizedNames = trimmedSectionNames.map((sectionName) =>
      sectionName.toLowerCase(),
    );
    const hasDuplicateWithinIncoming =
      new Set(normalizedNames).size !== normalizedNames.length;
    if (hasDuplicateWithinIncoming) {
      return;
    }

    const alreadyExists = trimmedSectionNames.find((sectionName) =>
      allSections.some(
        (section) =>
          String(section?.sectionName || "")
            .trim()
            .toLowerCase() === sectionName.toLowerCase(),
      ),
    );
    if (alreadyExists) {
      return;
    }

    const createdSectionNames = [];

    for (const sectionName of trimmedSectionNames) {
      const result = await dispatch(
        addDealSectionMeta({ dealId, sectionName }),
      );
      if (result?.meta?.requestStatus !== "fulfilled") {
        message.error(
          result?.payload?.error ||
            result?.payload ||
            `Failed to create section: ${sectionName}.`,
        );
        return;
      }

      const createdSection = result?.payload || {};
      createdSectionNames.push(createdSection?.sectionName || sectionName);
    }

    const lastCreatedSection =
      createdSectionNames[createdSectionNames.length - 1];
    setSelectedSection(lastCreatedSection);
    setFieldTouched((prev) => ({ ...prev, selectedSection: true }));
    setInlineSectionNames([]);
    setShowInlineSectionAdd(false);
    message.success(
      createdSectionNames.length > 1
        ? `${createdSectionNames.length} sections added successfully.`
        : "Section added successfully.",
    );
  };

  const handleDeleteSectionFromDropdown = async (section) => {
    const sectionName = String(section?.sectionName || "").trim();
    const sectionId = section?.sectionId;
    if (!sectionId || !sectionName) return;

    const shouldDelete = window.confirm(`Delete section "${sectionName}"?`);
    if (!shouldDelete) return;

    const result = await dispatch(deleteDealSectionMeta({ sectionId, dealId }));
    if (result?.meta?.requestStatus !== "fulfilled") {
      message.error(
        <span>
          Before deleting the section, remove all blocks in{" "}
          <strong>{sectionName}</strong> tab.
        </span>,
      );
      return;
    }

    if (selectedSection === sectionName) {
      setSelectedSection("");
      setFieldTouched((prev) => ({ ...prev, selectedSection: true }));
    }
    message.success("Section deleted successfully.");
  };

  const getTopFieldErrors = () => ({
    selectedSection: selectedSection ? "" : "Section is required.",
    selectedTitle: (() => {
      const trimmedTitle = (selectedTitle || "").trim();

      if (!trimmedTitle) return "Title is required.";
      if (!isValidMeaningfulData(trimmedTitle))
        return "Please enter a valid and meaningful data.";
      if (trimmedTitle.length < 3)
        return "Title must be at least 3 characters.";
      if (trimmedTitle.length > 300)
        return "Title cannot exceed 300 characters.";
      if (!/[A-Za-z]/.test(trimmedTitle))
        return "Title must contain at least one alphabet.";

      const matchingSection = currentSections.find(
        (section) => section.sectionName === selectedSection,
      );
      const normalizedTitle = trimmedTitle.toLowerCase();
      const existingContentWithTitle =
        Array.isArray(matchingSection?.contents) &&
        matchingSection.contents.find((content) => {
          if (
            isEditMode &&
            String(content?.contentId) === String(initialData?.contentId)
          ) {
            return false;
          }

          return (
            String(content?.title || "")
              .trim()
              .toLowerCase() === normalizedTitle
          );
        });

      // Same title is allowed only when block type is the same (we append new blocks).
      if (
        existingContentWithTitle &&
        String(existingContentWithTitle?.blockType || "") !==
          String(selectedBlockType || "")
      ) {
        return "Title already exists in this tab.";
      }

      const duplicateTitleContentError = getDuplicateTitleContentError();
      if (duplicateTitleContentError) return duplicateTitleContentError;

      return "";
    })(),
    selectedBlockType: selectedBlockType ? "" : "Block type is required.",
  });

  const getBlockItemError = (blockItem, itemIndex) => {
    const getTextAreaValidationError = (rawValue, label) => {
      const value = String(rawValue || "");
      const trimmedValue = value.trim();

      if (!trimmedValue) return `${label} ${itemIndex + 1} is required.`;
      if (trimmedValue.length < 4)
        return `${label} ${itemIndex + 1} must be at least 4 characters.`;
      if (trimmedValue.length > 500)
        return `${label} ${itemIndex + 1} cannot exceed 500 characters.`;
      if (/<[^>]*>/.test(trimmedValue))
        return `${label} ${itemIndex + 1} cannot contain HTML or script tags.`;
      if (!/[A-Za-z]/.test(trimmedValue))
        return "Please enter a meaningful Data";
      if (!isValidMeaningfulData(trimmedValue))
        return "Please enter a meaningful Data";

      return "";
    };

    const getNumericValueValidationError = (rawValue, label) => {
      const value = String(rawValue || "").trim();

      if (!value) return `${label} ${itemIndex + 1} is required.`;
      if (!/^\d+$/.test(value))
        return `${label} ${itemIndex + 1} must contain only numbers.`;
      if (value.length > 15)
        return `${label} ${itemIndex + 1} cannot exceed 15 digits.`;

      return "";
    };

    switch (selectedBlockType) {
      case "Text": {
        return getTextAreaValidationError(blockItem?.text, "Text area");
      }

      case "Bullet list": {
        const bulletPointError = getTextAreaValidationError(
          blockItem?.item,
          "Bullet point",
        );
        if (bulletPointError) return bulletPointError;

        const normalizedBulletPoint = String(blockItem?.item || "")
          .trim()
          .toLowerCase();
        if (!normalizedBulletPoint) return "";

        const hasDuplicateInCurrentItems = blockItems.some(
          (item, idx) =>
            idx !== itemIndex &&
            String(item?.item || "")
              .trim()
              .toLowerCase() === normalizedBulletPoint,
        );

        if (hasDuplicateInCurrentItems)
          return `Bullet point ${itemIndex + 1}: Bullet list point already added.`;
        return "";
      }

      case "Card grid": {
        const missing = [];
        const blockTitle = blockItem?.blockTitle || "";
        const trimmedBlockTitle = blockTitle.trim();
        if (!trimmedBlockTitle) missing.push("card title");
        const cardValueError = getNumericValueValidationError(
          blockItem?.value,
          "Card value",
        );
        if (cardValueError) missing.push("value");
        if (trimmedBlockTitle && trimmedBlockTitle.length < 3)
          return `Card ${itemIndex + 1}: card title must be at least 3 characters.`;
        if (trimmedBlockTitle.length > 300)
          return `Card ${itemIndex + 1}: card title cannot exceed 300 characters.`;
        if (trimmedBlockTitle && !/[A-Za-z]/.test(trimmedBlockTitle))
          return `Card ${itemIndex + 1}: card title must contain at least one alphabet.`;
        if (cardValueError)
          return `Card ${itemIndex + 1}: ${cardValueError.replace(` ${itemIndex + 1}`, "")}`;
        if (missing.length === 0) {
          const normalizedCardTitle = trimmedBlockTitle.toLowerCase();
          const hasDuplicateCardTitle = blockItems.some(
            (item, idx) =>
              idx !== itemIndex &&
              String(item?.blockTitle || "")
                .trim()
                .toLowerCase() === normalizedCardTitle,
          );
          if (hasDuplicateCardTitle)
            return `Card ${itemIndex + 1}: Card block title should not repeat`;

          return "";
        }
        return `Card ${itemIndex + 1}: ${missing.join(" and ")} required.`;
      }

      case "Progress bar": {
        const labelValue = blockItem?.label || "";
        const trimmedLabel = labelValue.trim();
        const hasLabel = trimmedLabel.length > 0;
        const value = Number(blockItem?.value);
        const isValueValid =
          Number.isFinite(value) && value >= 0 && value <= 100;
        if (trimmedLabel && trimmedLabel.length < 3)
          return `Progress bar ${itemIndex + 1}: title must be at least 3 characters.`;
        if (trimmedLabel.length > 300)
          return `Progress bar ${itemIndex + 1}: title cannot exceed 300 characters.`;
        if (trimmedLabel && !/[A-Za-z]/.test(trimmedLabel))
          return `Progress bar ${itemIndex + 1}: title must contain at least one alphabet.`;
        if (trimmedLabel) {
          const normalizedProgressTitle = trimmedLabel.toLowerCase();
          const hasDuplicateInCurrentItems = blockItems.some(
            (item, idx) =>
              idx !== itemIndex &&
              String(item?.label || "")
                .trim()
                .toLowerCase() === normalizedProgressTitle,
          );
          if (hasDuplicateInCurrentItems) {
            return `Progress bar ${itemIndex + 1}: Progress bar title should not be same.`;
          }
        }
        if (hasLabel && isValueValid) return "";
        return `Progress bar ${itemIndex + 1}: title and value (0-100) are required.`;
      }

      case "Metrics": {
        const metricTitle = blockItem?.metricTitle || "";
        const trimmedMetricTitle = metricTitle.trim();
        const hasTitle = trimmedMetricTitle.length > 0;
        const rawMetricValue = String(blockItem?.value || "").trim();
        const metricValueError = (() => {
          if (!rawMetricValue) return "Metric value is required.";
          if (!/^\d+$/.test(rawMetricValue))
            return "Metric value must contain only numbers.";
          if (rawMetricValue.length > 15)
            return "Metric value cannot exceed 15 digits.";
          return "";
        })();
        const hasValue = !metricValueError;
        if (trimmedMetricTitle && trimmedMetricTitle.length < 3)
          return `Metric ${itemIndex + 1}: title must be at least 3 characters.`;
        if (trimmedMetricTitle.length > 300)
          return `Metric ${itemIndex + 1}: title cannot exceed 300 characters.`;
        if (trimmedMetricTitle && !/[A-Za-z]/.test(trimmedMetricTitle))
          return `Metric ${itemIndex + 1}: title must contain at least one alphabet.`;
        if (trimmedMetricTitle) {
          const normalizedMetricTitle = trimmedMetricTitle.toLowerCase();
          const hasDuplicateInCurrentItems = blockItems.some(
            (item, idx) =>
              idx !== itemIndex &&
              String(item?.metricTitle || "")
                .trim()
                .toLowerCase() === normalizedMetricTitle,
          );
          if (hasDuplicateInCurrentItems) {
            return `Metric ${itemIndex + 1}: Metric title should not be same.`;
          }
        }
        if (metricValueError)
          return `Metric ${itemIndex + 1}: ${metricValueError.replace(` ${itemIndex + 1}`, "")}`;
        if (hasTitle && hasValue) return "";
        return `Metric ${itemIndex + 1}: title and value are required.`;
      }

      case "Status": {
        const statusTitle = blockItem?.statusTitle || "";
        const trimmedStatusTitle = statusTitle.trim();
        const hasTitle = trimmedStatusTitle.length > 0;
        const statusDescriptionError = getTextAreaValidationError(
          blockItem?.description,
          "Status description",
        );
        const hasDescription = !statusDescriptionError;
        if (trimmedStatusTitle && trimmedStatusTitle.length < 3)
          return `Status ${itemIndex + 1}: title must be at least 3 characters.`;
        if (trimmedStatusTitle.length > 300)
          return `Status ${itemIndex + 1}: title cannot exceed 300 characters.`;
        if (trimmedStatusTitle && !/[A-Za-z]/.test(trimmedStatusTitle))
          return `Status ${itemIndex + 1}: title must contain at least one alphabet.`;
        if (trimmedStatusTitle) {
          const normalizedStatusTitle = trimmedStatusTitle.toLowerCase();
          const hasDuplicateInCurrentItems = blockItems.some(
            (item, idx) =>
              idx !== itemIndex &&
              String(item?.statusTitle || "")
                .trim()
                .toLowerCase() === normalizedStatusTitle,
          );
          if (hasDuplicateInCurrentItems) {
            return `Status ${itemIndex + 1}: Status title should not be same.`;
          }
        }
        if (statusDescriptionError)
          return `Status ${itemIndex + 1}: ${statusDescriptionError.replace(` ${itemIndex + 1}`, "")}`;
        if (hasTitle && hasDescription) return "";
        return `Status ${itemIndex + 1}: title and description are required.`;
      }

      case "Upload documents": {
        const fileName = String(blockItem?.fileName || "").trim();
        const fileUrl = String(blockItem?.fileUrl || "").trim();
        if (!fileName || !fileUrl) {
          return `Upload document ${itemIndex + 1}: file is required.`;
        }

        const normalizedFileName = fileName.toLowerCase();
        const hasDuplicateInCurrentItems = blockItems.some(
          (item, idx) =>
            idx !== itemIndex &&
            String(item?.fileName || "")
              .trim()
              .toLowerCase() === normalizedFileName,
        );
        if (hasDuplicateInCurrentItems) {
          return `Upload document ${itemIndex + 1}: same file already added.`;
        }

        return "";
      }

      default:
        return "";
    }
  };

  const topFieldErrors = getTopFieldErrors();
  const blockItemErrors = blockItems.map((item, idx) =>
    getBlockItemError(item, idx),
  );
  const hasTopLevelErrors = Object.values(topFieldErrors).some(Boolean);
  const hasBlockItemErrors = blockItemErrors.some(Boolean);
  const hasMissingBlockItems = selectedBlockType && blockItems.length === 0;
  const isFormValid =
    !hasTopLevelErrors && !hasBlockItemErrors && !hasMissingBlockItems;

  const shouldShowFieldError = (fieldKey) =>
    (submitAttempted || fieldTouched[fieldKey]) &&
    Boolean(topFieldErrors[fieldKey]);

  const hasBlockItemInteraction = (blockItem) => {
    switch (selectedBlockType) {
      case "Text":
        return Boolean(String(blockItem?.text || "").trim());
      case "Bullet list":
        return Boolean(String(blockItem?.item || "").trim());
      case "Card grid":
        return Boolean(
          String(blockItem?.blockTitle || "").trim() ||
          String(blockItem?.value || "").trim(),
        );
      case "Progress bar":
        return Boolean(
          String(blockItem?.label || "").trim() ||
          String(blockItem?.value ?? "").trim(),
        );
      case "Metrics":
        return Boolean(
          String(blockItem?.metricTitle || "").trim() ||
          String(blockItem?.value || "").trim(),
        );
      case "Status":
        return Boolean(
          String(blockItem?.statusTitle || "").trim() ||
          String(blockItem?.description || "").trim(),
        );
      case "Upload documents":
        return Boolean(
          String(blockItem?.fileName || "").trim() ||
          String(blockItem?.fileUrl || "").trim(),
        );
      default:
        return false;
    }
  };

  const handleCreate = async () => {
    setSubmitAttempted(true);
    const duplicateTitleContentError = getDuplicateTitleContentError();
    if (duplicateTitleContentError) {
      message.error(duplicateTitleContentError);
      handleClose();
      return;
    }
    if (!isFormValid) return;

    const findOptionId = (type, value) =>
      allOptions.find(
        (o) =>
          normalizedType(o.optionType) === normalizedType(type) &&
          o.optionValue === value,
      )?.optionId;

    const trimmedTitle = (selectedTitle || "").trim();
    const selectedBlockTypeMeta = blockTypes.find(
      (bt) => bt.blockTypeName === selectedBlockType,
    );
    const matchingSection = currentSections.find(
      (section) => section.sectionName === selectedSection,
    );
    const existingContentWithSameTitle = Array.isArray(
      matchingSection?.contents,
    )
      ? matchingSection.contents.find(
          (content) =>
            String(content?.title || "")
              .trim()
              .toLowerCase() === trimmedTitle.toLowerCase(),
        )
      : null;
    const shouldAppendToExisting =
      !isEditMode &&
      existingContentWithSameTitle &&
      String(existingContentWithSameTitle?.blockType || "") ===
        String(selectedBlockType || "");

    const buildBlocks = () => {
      switch (selectedBlockType) {
        case "Text":
          return blockItems.map((b) => ({ text: (b.text || "").trim() }));
        case "Bullet list":
          return blockItems.map((b) => ({
            item: (b.item || "").trim(),
            bulletTypeId: findOptionId("BULLET_PREFIX", b.prefixType),
          }));
        case "Card grid":
          return blockItems.map((b) => ({
            blockTitle: (b.blockTitle || "").trim(),
            iconTypeId: findOptionId("BULLET_PREFIX", b.iconType),
            iconColorId: findOptionId("BULLET_COLOR", b.iconColor),
            value: (b.value || "").trim(),
          }));
        case "Progress bar":
          return blockItems.map((b) => ({
            label: (b.label || "").trim(),
            value: String(b.value),
            barColorId: findOptionId("BULLET_COLOR", b.barColor),
          }));
        case "Metrics":
          return blockItems.map((b) => ({
            metricTitle: (b.metricTitle || "").trim(),
            prefixId: findOptionId("METRIC_PREFIX", b.prefix),
            value: (b.value || "").trim(),
            suffixId: findOptionId("METRIC_SUFFIX", b.suffix),
          }));
        case "Status":
          return blockItems.map((b) => ({
            statusTitle: (b.statusTitle || "").trim(),
            description: (b.description || "").trim(),
            statusId: findOptionId("STATUS", b.status),
          }));
        case "Upload documents":
          return blockItems.map((b) => ({
            fileName: (b.fileName || "").trim(),
            fileUrl: (b.fileUrl || "").trim(),
            fileType: (b.fileType || "").trim(),
          }));
        default:
          return blockItems;
      }
    };

    const existingBlocks = Array.isArray(existingContentWithSameTitle?.blocks)
      ? existingContentWithSameTitle.blocks
      : [];
    const newBlocks = buildBlocks();
    const mergedBlocks = shouldAppendToExisting
      ? [...existingBlocks, ...newBlocks]
      : newBlocks;

    const payload = {
      ...(isEditMode || shouldAppendToExisting
        ? {
            contentId: shouldAppendToExisting
              ? existingContentWithSameTitle?.contentId
              : initialData?.contentId,
          }
        : { dealId }),
      dealId,
      sectionId: selectedSectionMeta?.sectionId,
      sectionName: selectedSection,
      title: trimmedTitle,
      blockTypeId: selectedBlockTypeMeta?.blockTypeId,
      displayOrder: selectedSectionMeta?.displayOrder,
      ...(selectedBlockType === "Bullet list" && {
        bulletColorId: findOptionId("BULLET_COLOR", selectedBulletColor),
      }),
      blocks: mergedBlocks,
    };

    const result = await dispatch(
      isEditMode || shouldAppendToExisting
        ? updateDealSection(payload)
        : addDealSection(payload),
    );
    if (result?.meta?.requestStatus !== "fulfilled") {
      message.error(
        isEditMode || shouldAppendToExisting
          ? "Failed to update section. Please try again."
          : "Failed to add section. Please try again.",
      );
      return;
    }

    message.success(
      isEditMode || shouldAppendToExisting
        ? "Section updated successfully."
        : "Section added successfully.",
    );
    onCreate({
      contentId: shouldAppendToExisting
        ? existingContentWithSameTitle?.contentId
        : initialData?.contentId,
      sectionName: selectedSection,
      sectionId: selectedSectionMeta?.sectionId,
      position: selectedSectionMeta?.displayOrder,
      title: trimmedTitle,
      blockType: selectedBlockType,
      blocks: mergedBlocks,
      bulletColor:
        selectedBlockType === "Bullet list" ? selectedBulletColor : undefined,
    });
    resetForm();
  };

  const renderFormComponent = () => {
    switch (selectedBlockType) {
      case "Text":
        return blockItems.map((blockItem, itemIndex) => (
          <div
            className="metric-block-card"
            key={`${selectedBlockType}-${itemIndex}`}
          >
            <TextForm
              blockItems={blockItems}
              itemIndex={itemIndex}
              blockItem={blockItem}
              onUpdateBlockItem={updateBlockItem}
              onDeleteBlockItem={handleDeleteBlockItem}
            />
          </div>
        ));

      case "Bullet list":
        return (
          <>
            <div className="form-group bullet-color-group">
              <label>Bullet color (Default color is black)</label>
              <div className="bullet-color-select-wrap">
                <span
                  className="bullet-color-swatch"
                  style={{
                    background: getColorHex(
                      selectedBulletColor,
                      bulletColorOptions,
                    ),
                  }}
                />
                <CustomSelect
                  className="bullet-color-select"
                  options={bulletColorOptions.map((opt) => ({
                    value: opt.optionValue,
                    label: opt.optionLabel,
                  }))}
                  value={selectedBulletColor}
                  onChange={(value) => setSelectedBulletColor(value)}
                />
              </div>
            </div>
            {blockItems.map((blockItem, itemIndex) => (
              <div
                className="metric-block-card"
                key={`${selectedBlockType}-${itemIndex}`}
              >
                <BulletListForm
                  itemIndex={itemIndex}
                  blockItem={blockItem}
                  selectedBulletColor={selectedBulletColor}
                  bulletColorOptions={bulletColorOptions}
                  prefixTypeOptions={prefixTypeOptions}
                  onUpdateBlockItem={updateBlockItem}
                  onDeleteBlockItem={handleDeleteBlockItem}
                  onColorChange={setSelectedBulletColor}
                  isFirstItem={itemIndex === 0}
                />
              </div>
            ))}
          </>
        );

      case "Card grid":
        return blockItems.map((blockItem, itemIndex) => (
          <div
            className="metric-block-card"
            key={`${selectedBlockType}-${itemIndex}`}
          >
            <CardGridForm
              itemIndex={itemIndex}
              blockItem={blockItem}
              iconColorOptions={iconColorOptions}
              iconTypeOptions={iconTypeOptions}
              onUpdateBlockItem={updateBlockItem}
              onDeleteBlockItem={handleDeleteBlockItem}
            />
          </div>
        ));

      case "Progress bar":
        return blockItems.map((blockItem, itemIndex) => (
          <div
            className="metric-block-card"
            key={`${selectedBlockType}-${itemIndex}`}
          >
            <ProgressBarForm
              itemIndex={itemIndex}
              blockItem={blockItem}
              barColorOptions={barColorOptions}
              onUpdateBlockItem={updateBlockItem}
              onDeleteBlockItem={handleDeleteBlockItem}
            />
          </div>
        ));

      case "Metrics":
        return blockItems.map((blockItem, itemIndex) => (
          <div
            className="metric-block-card"
            key={`${selectedBlockType}-${itemIndex}`}
          >
            <MetricsForm
              itemIndex={itemIndex}
              blockItem={blockItem}
              prefixOptions={metricPrefixOptions}
              suffixOptions={metricSuffixOptions}
              onUpdateBlockItem={updateBlockItem}
              onDeleteBlockItem={handleDeleteBlockItem}
            />
          </div>
        ));

      case "Status":
        return blockItems.map((blockItem, itemIndex) => (
          <div
            className="metric-block-card"
            key={`${selectedBlockType}-${itemIndex}`}
          >
            <StatusForm
              itemIndex={itemIndex}
              blockItem={blockItem}
              statusOptions={statusOptions}
              onUpdateBlockItem={updateBlockItem}
              onDeleteBlockItem={handleDeleteBlockItem}
            />
          </div>
        ));

      case "Upload documents":
        return blockItems.map((blockItem, itemIndex) => (
          <div
            className="metric-block-card"
            key={`${selectedBlockType}-${itemIndex}`}
          >
            <UploadDocumentsForm
              itemIndex={itemIndex}
              blockItem={blockItem}
              dealId={dealId}
              onUpdateBlockItem={updateBlockItem}
              onDeleteBlockItem={handleDeleteBlockItem}
              showDelete={blockItems.length > 1}
            />
          </div>
        ));

      default:
        return null;
    }
  };

  if (!open) return null;

  return (
    <div className="add-section-modal-overlay" onClick={handleClose}>
      <div className="add-section-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Add Block section</h3>

        <div className="form-group">
          <label>
            Section <span className="required-mark">*</span>
          </label>
          <CustomSelect
            placeholder="Select a section"
            options={dropdownSections.map((section) => ({
              value: section.sectionName,
              label: section.sectionName,
            }))}
            optionRender={(option) => renderSectionDropdownOption(option)}
            value={selectedSection || undefined}
            disabled={isEditMode || loading}
            popupClassName="section-dropdown-panel"
            getPopupContainer={(triggerNode) => triggerNode.parentElement}
            onOpenChange={handleSectionDropdownOpenChange}
            dropdownRender={(menu) => (
              <div className="section-dropdown-wrap">
                {menu}
                {!isEditMode && (
                  <div
                    className="section-dropdown-actions"
                    onMouseDown={(event) => {
                      const targetTag = String(
                        event.target?.tagName || "",
                      ).toLowerCase();
                      if (targetTag !== "input" && targetTag !== "textarea") {
                        event.preventDefault();
                      }
                      event.stopPropagation();
                    }}
                  >
                    <button
                      type="button"
                      className="section-inline-plus"
                      disabled={loading || addDealSectionMetaLoading}
                      onClick={handleAddInlineSectionRow}
                    >
                      <PlusOutlined />
                    </button>
                    {showInlineSectionAdd && (
                      <div className="section-inline-add-list">
                        {inlineSectionNames.map((sectionName, index) => (
                          <div
                            className="section-inline-add-row"
                            key={`inline-section-row-${index}`}
                          >
                            <input
                              type="text"
                              placeholder={`Enter section ${index + 1}`}
                              value={sectionName}
                              maxLength={150}
                              disabled={loading || addDealSectionMetaLoading}
                              onChange={(event) =>
                                handleInlineSectionNameChange(
                                  index,
                                  event.target.value,
                                )
                              }
                              onKeyDown={(event) => {
                                if (
                                  event.key === "Enter" &&
                                  canSubmitInlineSections
                                ) {
                                  event.preventDefault();
                                  handleAddSectionInsideDropdown();
                                }
                              }}
                            />
                            <button
                              type="button"
                              className="section-inline-delete"
                              disabled={loading || addDealSectionMetaLoading}
                              onClick={() =>
                                handleDeleteInlineSectionRow(index)
                              }
                              aria-label={`Delete section input ${index + 1}`}
                            >
                              <DeleteOutlined />
                            </button>
                          </div>
                        ))}
                        <CustomButton
                          type="primary"
                          htmlType="button"
                          className="section-inline-submit"
                          disabled={
                            loading ||
                            addDealSectionMetaLoading ||
                            !canSubmitInlineSections
                          }
                          loading={addDealSectionMetaLoading}
                          onClick={handleAddSectionInsideDropdown}
                        >
                          Submit
                        </CustomButton>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            status={
              shouldShowFieldError("selectedSection") ? "error" : undefined
            }
            onChange={(value) => {
              setSelectedSection(value);
              setFieldTouched((prev) => ({ ...prev, selectedSection: true }));
            }}
          />
          {shouldShowFieldError("selectedSection") && (
            <p className="form-error-text">{topFieldErrors.selectedSection}</p>
          )}
        </div>

        <div className="form-group">
          <label>
            Title <span className="required-mark">*</span>
          </label>
          <input
            type="text"
            placeholder="Enter title"
            value={selectedTitle}
            maxLength={300}
            disabled={loading}
            className={
              shouldShowFieldError("selectedTitle") ? "input-error" : ""
            }
            onChange={(event) => {
              setSelectedTitle(event.target.value);
              setFieldTouched((prev) => ({ ...prev, selectedTitle: true }));
            }}
          />
          <div className="error-counter-row">
            {shouldShowFieldError("selectedTitle") ? (
              <p className="form-error-text">{topFieldErrors.selectedTitle}</p>
            ) : (
              <span className="error-placeholder" />
            )}
            <span className="char-counter-text">
              {selectedTitle.length}/300
            </span>
          </div>
          {selectedTitle.length >= 300 && (
            <p className="char-limit-note">Max 300 characters reached</p>
          )}
        </div>

        <div className="form-group">
          <label>
            Block Type <span className="required-mark">*</span>
          </label>
          <CustomSelect
            placeholder="Select a block type"
            options={blockTypeOptions.map((opt) => ({
              value: opt,
              label: opt,
            }))}
            value={selectedBlockType || undefined}
            disabled={isEditMode || loading}
            status={
              shouldShowFieldError("selectedBlockType") ? "error" : undefined
            }
            onChange={(value) => handleBlockTypeChange(value)}
          />
          {shouldShowFieldError("selectedBlockType") && (
            <p className="form-error-text">
              {topFieldErrors.selectedBlockType}
            </p>
          )}
        </div>

        {selectedBlockType && (
          <>
            {renderFormComponent()}
            {blockItemErrors.map((errorText, itemIndex) =>
              errorText ? (
                submitAttempted ||
                hasBlockItemInteraction(blockItems[itemIndex]) ? (
                  <p
                    key={`item-error-${itemIndex}`}
                    className="form-error-text block-item-error-text"
                  >
                    {errorText}
                  </p>
                ) : null
              ) : null,
            )}
            {submitAttempted && hasMissingBlockItems && (
              <p className="form-error-text">Add at least one block item.</p>
            )}
            <button
              type="button"
              className="icon-add-btn"
              onClick={handleAddBlockItem}
            >
              <PlusOutlined />
            </button>
          </>
        )}

        <div className="modal-footer-actions">
          <CancelButton
            type="button"
            className="btn-modal btn-modal-cancel"
            onClick={handleClose}
          >
            Cancel
          </CancelButton>
          <CustomButton
            type="primary"
            className="btn-modal btn-modal-create"
            loading={
              isEditMode ? updateDealSectionLoading : addDealSectionLoading
            }
            onClick={handleCreate}
          >
            {isEditMode ? "Update" : "Create"}
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default AddSectionModal;
